import apiClient from './apiClient';

/**
 * Auth API
 */
export const authApi = {
  /**
   * Registrazione nuovo tenant
   */
  register: async (data) => {
    const response = await apiClient.post('/auth/register', data);
    return response.data;
  },

  /**
   * Login
   */
  login: async (email, password) => {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  /**
   * Logout
   */
  logout: async () => {
    const response = await apiClient.post('/auth/logout');
    return response.data;
  },

  /**
   * Logout da tutti i device
   */
  logoutAll: async () => {
    const response = await apiClient.post('/auth/logout-all');
    return response.data;
  },

  /**
   * Get profilo utente corrente
   */
  getMe: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  /**
   * Cambio password
   */
  changePassword: async (currentPassword, newPassword) => {
    const response = await apiClient.patch('/auth/me/password', {
      currentPassword,
      newPassword
    });
    return response.data;
  },

  /**
   * Refresh token (chiamato automaticamente dall'interceptor)
   */
  refresh: async () => {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  }
};

/**
 * Projects API
 */
export const projectsApi = {
  getAll: async (params) => {
    const response = await apiClient.get('/projects', { params });
    return response.data;
  },

  getOne: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await apiClient.post('/projects', data);
    return response.data;
  },

  update: async (projectId, data) => {
    const response = await apiClient.patch(`/projects/${projectId}`, data);
    return response.data;
  },

  updateStatus: async (projectId, status, reason = null) => {
    const response = await apiClient.patch(`/projects/${projectId}/status`, {
      status,
      reason
    });
    return response.data;
  },

  delete: async (projectId) => {
    const response = await apiClient.delete(`/projects/${projectId}`);
    return response.data;
  },

  getSteps: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}/steps`);
    return response.data;
  }
};

/**
 * Project Steps API (DEPRECATED - use contractsApi)
 */
export const stepsApi = {
  getStep: async (projectId, stepNumber) => {
    const response = await apiClient.get(`/projects/${projectId}/steps/${stepNumber}`);
    return response.data;
  },

  execute: async (projectId, stepNumber, inputData) => {
    const response = await apiClient.post(
      `/projects/${projectId}/steps/${stepNumber}/execute`,
      inputData
    );
    return response.data;
  },

  approve: async (projectId, stepNumber) => {
    const response = await apiClient.post(
      `/projects/${projectId}/steps/${stepNumber}/approve`
    );
    return response.data;
  },

  regenerate: async (projectId, stepNumber, instructions = null) => {
    const response = await apiClient.post(
      `/projects/${projectId}/steps/${stepNumber}/regenerate`,
      { instructions }
    );
    return response.data;
  },

  reset: async (projectId, stepNumber) => {
    const response = await apiClient.post(
      `/projects/${projectId}/steps/${stepNumber}/reset`
    );
    return response.data;
  },

  updateInput: async (projectId, stepNumber, inputData) => {
    const response = await apiClient.patch(
      `/projects/${projectId}/steps/${stepNumber}/input`,
      inputData
    );
    return response.data;
  },

  updateOutput: async (projectId, stepNumber, outputData) => {
    const response = await apiClient.patch(
      `/projects/${projectId}/steps/${stepNumber}/output`,
      outputData
    );
    return response.data;
  }
};

/**
 * Contracts API (NEW - 27 contracts 0-26)
 */
export const contractsApi = {
  /**
   * Get all contracts for a project
   */
  getAll: async (projectId) => {
    const response = await apiClient.get(`/projects/${projectId}/contracts`);
    return response.data;
  },

  /**
   * Get specific contract
   */
  getOne: async (projectId, contractNum) => {
    const response = await apiClient.get(`/projects/${projectId}/contracts/${contractNum}`);
    return response.data;
  },

  /**
   * Get all executions for a contract
   */
  getExecutions: async (projectId, contractNum, params) => {
    const response = await apiClient.get(
      `/projects/${projectId}/contracts/${contractNum}/executions`,
      { params }
    );
    return response.data;
  },

  /**
   * Get specific execution details
   */
  getExecution: async (projectId, contractNum, execId) => {
    const response = await apiClient.get(
      `/projects/${projectId}/contracts/${contractNum}/executions/${execId}`
    );
    return response.data;
  },

  /**
   * Execute a contract
   */
  execute: async (projectId, contractNum, options = {}) => {
    const response = await apiClient.post(
      `/projects/${projectId}/contracts/${contractNum}/execute`,
      options
    );
    return response.data;
  },

  /**
   * Approve an execution
   */
  approve: async (projectId, contractNum, execId, notes = null, curatedOutput = null) => {
    const body = { notes };
    if (curatedOutput) body.curatedOutput = curatedOutput;
    const response = await apiClient.post(
      `/projects/${projectId}/contracts/${contractNum}/executions/${execId}/approve`,
      body
    );
    return response.data;
  },

  /**
   * Reject an execution
   */
  reject: async (projectId, contractNum, execId, reason) => {
    const response = await apiClient.post(
      `/projects/${projectId}/contracts/${contractNum}/executions/${execId}/reject`,
      { reason }
    );
    return response.data;
  },

  /**
   * Submit human review output (Contract 12)
   */
  submitReview: async (projectId, contractNum, execId, reviewData) => {
    const response = await apiClient.post(
      `/projects/${projectId}/contracts/${contractNum}/executions/${execId}/submit-review`,
      reviewData
    );
    return response.data;
  },

  /**
   * Regenerate (create alternative exploration)
   */
  regenerate: async (projectId, contractNum, execId, options = {}) => {
    const response = await apiClient.post(
      `/projects/${projectId}/contracts/${contractNum}/executions/${execId}/regenerate`,
      options
    );
    return response.data;
  },

  /**
   * Get contract tree (parent-child relationships)
   */
  getTree: async (projectId, contractNum) => {
    const response = await apiClient.get(
      `/projects/${projectId}/contracts/${contractNum}/tree`
    );
    return response.data;
  },

  /**
   * Controlla se il job asincrono OpenAI è completato e scarica il risultato
   */
  checkAIStatus: async (projectId, contractNum) => {
    const response = await apiClient.post(
      `/projects/${projectId}/contracts/${contractNum}/check-ai-status`
    );
    return response.data;
  },

  checkVeoStatus: async (projectId) => {
    const response = await apiClient.post(`/projects/${projectId}/check-veo-status`);
    return response.data;
  },

  submitVeoJob: async (projectId, jobId, durationSeconds = null) => {
    const body = { jobId };
    if (durationSeconds !== null) body.durationSeconds = durationSeconds;
    const response = await apiClient.post(`/projects/${projectId}/veo-submit-job`, body);
    return response.data;
  },

  extendVeoJob: async (projectId, jobId, durationSeconds = null) => {
    const body = { jobId };
    if (durationSeconds !== null) body.durationSeconds = durationSeconds;
    const response = await apiClient.post(`/projects/${projectId}/veo-extend-job`, body);
    return response.data;
  },

  discardVeoVideo: async (projectId) => {
    const response = await apiClient.post(`/projects/${projectId}/veo-discard`);
    return response.data;
  },

  resubmitVeo: async (projectId, jobId = null) => {
    const response = await apiClient.post(`/projects/${projectId}/veo-resubmit`, { jobId });
    return response.data;
  },

  rebuildVeoJobs: async (projectId, force = false) => {
    const response = await apiClient.post(`/projects/${projectId}/veo-rebuild-jobs`, { force });
    return response.data;
  },

  updateVeoClip: async (projectId, jobId, clipIndex, text, instruction) => {
    const response = await apiClient.put(`/projects/${projectId}/veo-update-clip`, { jobId, clipIndex, text, instruction });
    return response.data;
  },

  deleteVeoClip: async (projectId, jobId, clipIndex) => {
    const response = await apiClient.post(`/projects/${projectId}/veo-delete-clip`, { jobId, clipIndex });
    return response.data;
  },

  regenerateVeoClip: async (projectId, jobId, clipIndex, durationSeconds = null) => {
    const body = { jobId, clipIndex };
    if (durationSeconds !== null) body.durationSeconds = durationSeconds;
    const response = await apiClient.post(`/projects/${projectId}/veo-regenerate-clip`, body);
    return response.data;
  },

  resetContractsFrom: async (projectId, contractNum) => {
    const response = await apiClient.post(`/projects/${projectId}/reset-contracts-from`, { contractNum });
    return response.data;
  }
};

/**
 * Plans API (pubblico)
 */
export const plansApi = {
  getAll: async () => {
    const response = await apiClient.get('/plans');
    return response.data;
  },

  getOne: async (planId) => {
    const response = await apiClient.get(`/plans/${planId}`);
    return response.data;
  }
};

/**
 * Users API (admin tenant)
 */
export const usersApi = {
  getAll: async (params) => {
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  getOne: async (userId) => {
    const response = await apiClient.get(`/users/${userId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await apiClient.post('/users', data);
    return response.data;
  },

  update: async (userId, data) => {
    const response = await apiClient.patch(`/users/${userId}`, data);
    return response.data;
  },

  updateRole: async (userId, role) => {
    const response = await apiClient.patch(`/users/${userId}/role`, { role });
    return response.data;
  },

  updateStatus: async (userId, isActive) => {
    const response = await apiClient.patch(`/users/${userId}/status`, { isActive });
    return response.data;
  },

  delete: async (userId) => {
    const response = await apiClient.delete(`/users/${userId}`);
    return response.data;
  }
};

/**
 * Subscriptions API
 */
export const subscriptionsApi = {
  getMy: async () => {
    const response = await apiClient.get('/subscriptions/my');
    return response.data;
  },

  createOrUpdate: async (planId) => {
    const response = await apiClient.post('/subscriptions', { planId });
    return response.data;
  },

  cancelMy: async () => {
    const response = await apiClient.patch('/subscriptions/my/cancel');
    return response.data;
  },

  reactivateMy: async () => {
    const response = await apiClient.patch('/subscriptions/my/reactivate');
    return response.data;
  }
};

/**
 * Billing API
 */
export const billingApi = {
  getMy: async () => {
    const response = await apiClient.get('/billing/my');
    return response.data;
  },

  createMy: async (data) => {
    const response = await apiClient.post('/billing/my', data);
    return response.data;
  },

  updateMy: async (data) => {
    const response = await apiClient.patch('/billing/my', data);
    return response.data;
  },

  getInvoices: async () => {
    const response = await apiClient.get('/billing/my/invoices');
    return response.data;
  },

  downloadInvoice: async (invoiceId) => {
    const response = await apiClient.get(`/billing/my/invoices/${invoiceId}`, {
      responseType: 'blob'
    });
    return response.data;
  }
};

/**
 * Tenants API (superadmin only)
 */
export const tenantsApi = {
  getAll: async (params) => {
    const response = await apiClient.get('/tenants', { params });
    return response.data;
  },

  getOne: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await apiClient.post('/tenants', data);
    return response.data;
  },

  update: async (tenantId, data) => {
    const response = await apiClient.patch(`/tenants/${tenantId}`, data);
    return response.data;
  },

  updateStatus: async (tenantId, status) => {
    const response = await apiClient.patch(`/tenants/${tenantId}/status`, { status });
    return response.data;
  },

  getUsage: async (tenantId) => {
    const response = await apiClient.get(`/tenants/${tenantId}/usage`);
    return response.data;
  }
};

/**
 * AI Prompts API (superadmin only)
 */
export const aiPromptsApi = {
  getAll: async (params) => {
    const response = await apiClient.get('/ai-prompts', { params });
    return response.data;
  },

  getOne: async (key) => {
    const response = await apiClient.get(`/ai-prompts/${key}`);
    return response.data;
  },

  update: async (key, data) => {
    const response = await apiClient.patch(`/ai-prompts/${key}`, data);
    return response.data;
  },

  updateModel: async (key, defaultModelId) => {
    const response = await apiClient.patch(`/ai-prompts/${key}/model`, { defaultModelId });
    return response.data;
  },

  getHistory: async (key) => {
    const response = await apiClient.get(`/ai-prompts/${key}/history`);
    return response.data;
  },

  restoreVersion: async (key, version) => {
    const response = await apiClient.post(`/ai-prompts/${key}/restore/${version}`);
    return response.data;
  },

  test: async (key, testData) => {
    const response = await apiClient.post(`/ai-prompts/${key}/test`, testData);
    return response.data;
  }
};

/**
 * AI Models API (superadmin only)
 */
export const aiModelsApi = {
  getAll: async (params) => {
    const response = await apiClient.get('/ai-models', { params });
    return response.data;
  },

  getOne: async (modelId) => {
    const response = await apiClient.get(`/ai-models/${modelId}`);
    return response.data;
  },

  create: async (data) => {
    const response = await apiClient.post('/ai-models', data);
    return response.data;
  },

  update: async (modelId, data) => {
    const response = await apiClient.patch(`/ai-models/${modelId}`, data);
    return response.data;
  },

  updateStatus: async (modelId, isActive) => {
    const response = await apiClient.patch(`/ai-models/${modelId}/status`, { isActive });
    return response.data;
  }
};
